import {
    CENTER_REGION_ID,
    DOCK_ZONES_LIST,
    PANEL_KIND_CENTER,
    normalizeDockZone,
} from './constants.js';
import { findNode } from './layoutTree.js';
import {
    findRegionForNodeId,
    getLayoutRegionForPanel,
    resolveHostLayoutRegion,
} from './layoutRegions.js';

/**
 * @param {string | string[] | null | undefined} raw
 * @returns {string[] | null}
 */
export function normalizeZoneList(raw) {
    if (raw == null || raw === '') return null;
    const arr = Array.isArray(raw) ? raw : [raw];
    const out = arr
        .map((z) => normalizeDockZone(z))
        .filter((z) => DOCK_ZONES_LIST.includes(z));
    return out.length ? out : null;
}

/**
 * @param {{ allowedZones?: string | string[], disallowedZones?: string | string[] }} def
 * @returns {{ allowed: string[] | null, disallowed: string[] | null }}
 */
export function resolveZoneConstraints(def) {
    const allowed = normalizeZoneList(def?.allowedZones);
    const disallowed = allowed ? null : normalizeZoneList(def?.disallowedZones);
    return { allowed, disallowed };
}

/**
 * @param {string} panelId
 * @param {string} zone
 * @param {Record<string, { allowedZones?: string[], disallowedZones?: string[] }>} registry
 */
export function canPanelDockInZone(panelId, zone, registry = {}) {
    const z = normalizeDockZone(zone);
    const def = registry[panelId];
    if (!def) return true;

    const { allowed, disallowed } = resolveZoneConstraints(def);
    if (allowed?.length) {
        return allowed.includes(z);
    }
    if (disallowed?.length) {
        return !disallowed.includes(z);
    }
    return true;
}

function targetPanelIdFromHit(hit, root) {
    const direct = hit.targetPanelId || hit.panelId;
    if (direct) return direct;
    if (!root || !hit.nodeId) return null;
    const node = findNode(root, hit.nodeId)?.node;
    if (node?.type === 'panel') return node.panelId || null;
    return null;
}

/**
 * Map a drop hit to a top-level layout region for allowed/disallowed zone checks.
 * Host sub-zones on panels/tab groups outside the center band are local geometry only.
 * Hosts inside the layout center band (including a solo center panel like Editor) count
 * as the center region for constraints.
 * @param {{ tier?: string, region?: string, zone?: string, hostType?: string, nodeId?: string, panelId?: string, targetPanelId?: string }} hit
 * @param {{ root?: object, regions?: Record<string, string|null>, panelZoneMap?: Record<string, string> }} [context]
 */
export function dropHitToConstraintZone(hit, context = {}) {
    if (!hit) return null;
    if (hit.tier === 'root' && hit.region) {
        return normalizeDockZone(hit.region);
    }
    const isHostHit =
        hit.tier === 'host' ||
        (hit.tier == null && (hit.hostType || hit.nodeId));
    if (!isHostHit) return null;

    const { root, regions, panelZoneMap } = context;
    let layoutRegion = null;
    if (root && regions) {
        layoutRegion = resolveHostLayoutRegion(root, regions, hit);
        if (layoutRegion === 'center') {
            return 'center';
        }
        if (layoutRegion != null) {
            return null;
        }
    } else if (
        hit.hostType === 'center' ||
        hit.nodeId === CENTER_REGION_ID
    ) {
        return 'center';
    }

    const targetPanelId = targetPanelIdFromHit(hit, root);
    if (targetPanelId && panelZoneMap?.[targetPanelId] === 'center') {
        return 'center';
    }
    if (targetPanelId && root && regions) {
        const panelRegion = getLayoutRegionForPanel(
            root,
            regions,
            targetPanelId,
        );
        if (panelRegion === 'center') {
            return 'center';
        }
    }

    if (root && regions && hit.nodeId) {
        if (findRegionForNodeId(root, regions, hit.nodeId) === 'center') {
            return 'center';
        }
    }

    return null;
}

/**
 * Whether a drop target lies in the layout center band (editor / center slot),
 * not merely a host sub-zone named "center" on a side-region tab group.
 */
export function isCenterLayoutBandTarget(hit, context = {}, registry = {}) {
    if (!hit) return false;
    if (hit.hostType === 'center' || hit.nodeId === CENTER_REGION_ID) {
        return true;
    }

    const { root, regions, panelZoneMap } = context;
    const targetId = targetPanelIdFromHit(hit, root);

    if (targetId) {
        if (panelZoneMap?.[targetId] === 'center') return true;
        const reg = registry[targetId];
        if (reg?.panelKind === PANEL_KIND_CENTER) return true;
        const { allowed } = resolveZoneConstraints(reg || {});
        if (allowed?.length === 1 && allowed[0] === 'center') return true;
        if (root && regions) {
            if (getLayoutRegionForPanel(root, regions, targetId) === 'center') {
                return true;
            }
        }
    }

    if (root && regions && hit.nodeId) {
        if (findRegionForNodeId(root, regions, hit.nodeId) === 'center') {
            return true;
        }
    }

    return false;
}

/**
 * Whether a dragged panel may use a drop target (overlay zone or commit hit).
 */
export function canDockPanelAtHit(panelId, hit, context = {}, registry = {}) {
    if (!panelId || !hit) return true;

    if (isCenterLayoutBandTarget(hit, context, registry)) {
        return canPanelDockInZone(panelId, 'center', registry);
    }

    const constraintZone = dropHitToConstraintZone(hit, context);
    if (constraintZone) {
        return canPanelDockInZone(panelId, constraintZone, registry);
    }

    return true;
}
